import React, { useMemo, useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { AuthLang } from './AuthModal';
import { t as tI18n } from '../utils/i18n';
import { extractPaletteFromImageUrls } from '../utils/extractImagePalette';
import { analyzePaletteHeuristic } from '../utils/paletteDna';
import { renderToneMappingUniversalPbr } from '../services/toneMappingService';
import { api } from '../services/apiClient';
import { RUNNINGHUB_OUTPUT_MAX_AGE_MS, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS } from '../utils/runningHubRetention';
import {
  createScopedRunningHubClientItemId,
  syncRunningHubHistoryItemToCloud,
  deleteRunningHubHistoryFromCloud,
} from '../utils/runningHubCloudHistory';
import { stripRunningHubPayloadForLocalStorage, isRunningHubListThumbUrl } from '../utils/runningHubHistoryLocalStorage';
import {
  writeRunningHubHistoryListToLocalStorage,
  stampRunningHubHistoryOwner,
} from '../utils/runningHubHistoryScope';
import { RunningHubHistoryListThumb } from './RunningHubHistoryListThumb';
import { useRunningHubLinkIndicator } from '../utils/useRunningHubLinkIndicator';
import { useCreditDeduction } from '../hooks/useCreditDeduction';
import { useRunningHubPanelHistory } from '../hooks/useRunningHubPanelHistory';
import { addWatermarkToImage, generatedImageDownloadFilename } from './Watermark';
import { RunningHubCosExpiryBadge } from './RunningHubCosExpiryBadge';
import { useToast } from './Toast';
import {
  getUnsupportedUploadImageFormatMessage,
  isSupportedUploadImageDataUrl,
  isSupportedUploadImageFile,
} from '../utils/supportedUploadImageFormat';
import {
  startCompareSliderPointerDrag,
  startCompareSliderTouchDrag,
} from '../utils/compareSliderPointer';
import { attachComparePanelPinchZoom } from '../utils/comparePanelPinchZoom';
import { createRunningHubClientItemId, type RunningHubRecoveryRequest } from '../utils/applyRunningHubJobRecovery';
import { useRunningHubPanelRecovery } from '../hooks/useRunningHubPanelRecovery';
import {
  toneMappingCompareAfter,
  toneMappingCompareBefore,
  toneMappingCompareSliderKey,
  buildToneMappingCurrentFromItem,
  mergeToneMappingCurrentFromHistory,
  pickToneMappingCurrentAfterDelete,
  pruneToneMappingCroppedResultCache,
  getToneMappingCroppedResultForId,
  toneMappingPreviewFieldsEqual,
  toneMappingP3Trace,
  toneMappingSnip80,
  type ToneMappingCurrentItem,
} from '../utils/toneMappingCurrentItem';

type ToneMappingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  language: AuthLang;
  /** 登录用户邮箱：与主界面渲染一致，供后端扣费与鉴权（Bearer + body.userEmail） */
  userEmail?: string | null;
  /** 登录后同步侧栏历史元数据到数据库（图片仍为 COS / RunningHub URL） */
  isAuthenticated?: boolean;
  recoveryRequest?: RunningHubRecoveryRequest | null;
};

type ToneHistoryItem = ToneMappingCurrentItem;

/** 写入历史/同步云端用：限制输入槽位图体积，避免 localStorage 爆配额与云端 slim 删掉快照 */
const TONE_HISTORY_SLOT_MAX_EDGE = 1024;
const TONE_HISTORY_SLOT_JPEG_Q = 0.82;

async function compressImageSrcForToneHistorySlot(
  src: string,
  ensureSafeUrl: (s: string) => Promise<string>,
): Promise<string> {
  const s = String(src || '').trim();
  if (!s) return s;
  if (/^https?:\/\//i.test(s) && s.length < 6000) return s;
  let loadSrc = s;
  if (/^https?:\/\//i.test(s)) {
    try {
      loadSrc = await ensureSafeUrl(s);
    } catch {
      return s;
    }
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        let w = img.naturalWidth || img.width || 1;
        let h = img.naturalHeight || img.height || 1;
        const maxE = Math.max(w, h);
        let tw = w;
        let th = h;
        if (maxE > TONE_HISTORY_SLOT_MAX_EDGE) {
          const sc = TONE_HISTORY_SLOT_MAX_EDGE / maxE;
          tw = Math.max(1, Math.round(w * sc));
          th = Math.max(1, Math.round(h * sc));
        }
        const c = document.createElement('canvas');
        c.width = tw;
        c.height = th;
        const ctx = c.getContext('2d');
        if (!ctx) return resolve(loadSrc);
        ctx.drawImage(img, 0, 0, tw, th);
        resolve(c.toDataURL('image/jpeg', TONE_HISTORY_SLOT_JPEG_Q));
      } catch {
        resolve(loadSrc);
      }
    };
    img.onerror = () => resolve(s);
    img.src = loadSrc;
  });
}

const cleanExpiredToneHistory = (items: ToneHistoryItem[]): ToneHistoryItem[] => {
  const now = Date.now();
  return items.filter((item) => now - item.timestamp < RUNNINGHUB_OUTPUT_MAX_AGE_MS);
};

/** 写入失败（常见：配额满）时按条数递减重试，避免静默丢档 */
function persistToneHistoryItems(items: ToneHistoryItem[], userEmail?: string | null): ToneHistoryItem[] {
  const write = (list: ToneHistoryItem[]) => {
    writeRunningHubHistoryListToLocalStorage('tone_mapping', userEmail, list as unknown as Record<string, unknown>[]);
    return list;
  };
  try {
    return write(items);
  } catch {
    for (let n = items.length; n >= 1; n--) {
      const slice = items.slice(0, n);
      try {
        write(slice);
        if (n < items.length) {
          console.warn('[ToneMapping] localStorage 配额不足，历史已缩减为', n, '条');
        }
        return slice;
      } catch {
        continue;
      }
    }
    console.warn('[ToneMapping] 无法写入色调映射历史');
    return items;
  }
}

function pickToneResultPanelIndex(columnCount: number): number {
  if (columnCount <= 1) return 0;
  if (columnCount === 2) return 1;
  if (columnCount === 3) return 2;
  return columnCount - 2;
}

/** 在固定外框内按原图宽高比 contain，避免竖图被拉满横条区域看起来像「横图裁切」 */
function fitImageRectInBox(boxW: number, boxH: number, imgW: number, imgH: number): { w: number; h: number } | null {
  if (!boxW || !boxH || !imgW || !imgH) return null;
  const s = Math.min(boxW / imgW, boxH / imgH);
  return { w: Math.max(1, Math.round(imgW * s)), h: Math.max(1, Math.round(imgH * s)) };
}

/**
 * 将源图按与 CSS object-fit: cover + center 等价的规则铺到 cw×ch，输出与底图同像素尺寸，对比时两图 scale 一致。
 */
/** 宽高比接近时视为一致，避免浮点/取整导致误判又去走 cover 二次裁切 */
const COMPARE_ASPECT_EPS = 0.004;

function coverImageToDataUrl(src: HTMLImageElement, cw: number, ch: number, mimeQuality = 0.92): string | null {
  if (!cw || !ch) return null;
  const iw = src.naturalWidth || src.width;
  const ih = src.naturalHeight || src.height;
  if (!iw || !ih) return null;
  const scale = Math.max(cw / iw, ch / ih);
  const rw = iw * scale;
  const rh = ih * scale;
  const ox = (cw - rw) / 2;
  const oy = (ch - rh) / 2;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(cw));
  c.height = Math.max(1, Math.round(ch));
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(src, 0, 0, iw, ih, ox, oy, rw, rh);
  try {
    return c.toDataURL('image/jpeg', mimeQuality);
  } catch {
    return null;
  }
}

/**
 * 将图像写入 dw×dh：与主图/对比框宽高比一致时只做均匀缩放（无二度 cover 裁切）；
 * 不一致时用 contain（黑边）而不是 cover，避免与另一侧裁切策略不一致导致「一边拉近景」。
 */
function resizeImageToCompareBoxDataUrl(
  src: HTMLImageElement,
  dw: number,
  dh: number,
  mimeQuality = 0.92,
): string | null {
  if (!dw || !dh) return null;
  const iw = src.naturalWidth || src.width;
  const ih = src.naturalHeight || src.height;
  if (!iw || !ih) return null;
  const arI = iw / ih;
  const arO = dw / dh;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(dw));
  c.height = Math.max(1, Math.round(dh));
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  if (Math.abs(arI - arO) <= COMPARE_ASPECT_EPS) {
    ctx.drawImage(src, 0, 0, iw, ih, 0, 0, c.width, c.height);
  } else {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, c.width, c.height);
    const scale = Math.min(dw / iw, dh / ih);
    const rw = iw * scale;
    const rh = ih * scale;
    const ox = (dw - rw) / 2;
    const oy = (dh - rh) / 2;
    ctx.drawImage(src, 0, 0, iw, ih, ox, oy, rw, rh);
  }
  try {
    return c.toDataURL('image/jpeg', mimeQuality);
  } catch {
    return null;
  }
}

/** 等价于 CSS object-fit: contain + center：不裁切，必要时留黑边，用于保持主图画幅一致 */
function containImageToDataUrl(src: HTMLImageElement, cw: number, ch: number, mimeQuality = 0.92): string | null {
  if (!cw || !ch) return null;
  const iw = src.naturalWidth || src.width;
  const ih = src.naturalHeight || src.height;
  if (!iw || !ih) return null;
  const scale = Math.min(cw / iw, ch / ih);
  const rw = iw * scale;
  const rh = ih * scale;
  const ox = (cw - rw) / 2;
  const oy = (ch - rh) / 2;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(cw));
  c.height = Math.max(1, Math.round(ch));
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  // 背景填充为黑色（与暗色 UI 一致；浅色模式下也更接近对比区观感）
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(src, 0, 0, iw, ih, ox, oy, rw, rh);
  try {
    return c.toDataURL('image/jpeg', mimeQuality);
  } catch {
    return null;
  }
}

function readImageNaturalSize(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    if (!src?.trim()) {
      resolve({ w: 0, h: 0 });
      return;
    }
    const img = new Image();
    img.onload = () =>
      resolve({
        w: img.naturalWidth || img.width || 0,
        h: img.naturalHeight || img.height || 0,
      });
    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = src;
  });
}

/**
 * 将源图矩形区域以 object-fit:cover 方式铺到 cw×ch（与 coverImageToDataUrl 一致，但支持子区域）。
 */
function coverRegionToDataUrl(
  img: HTMLImageElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  cw: number,
  ch: number,
  mimeQuality = 0.92,
): string | null {
  if (!cw || !ch || !sw || !sh) return null;
  const scale = Math.max(cw / sw, ch / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  const ox = (cw - dw) / 2;
  const oy = (ch - dh) / 2;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(cw));
  c.height = Math.max(1, Math.round(ch));
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, sx, sy, sw, sh, ox, oy, dw, dh);
  try {
    return c.toDataURL('image/jpeg', mimeQuality);
  } catch {
    return null;
  }
}

/**
 * 服务端有时返回「横向多列」拼图（参考图|结果…）；仅在几何上与主图列宽高比一致时才裁单列。
 * 旧逻辑用「宽/主图宽≥1.45」极易误判单张略宽的输出，裁成窄条导致比例全毁。
 */
async function cropToneMappingResultToSinglePanel(
  resultDataUrl: string,
  mainNormW: number,
  mainNormH: number,
): Promise<string> {
  if (!mainNormW || !mainNormH || !resultDataUrl) return resultDataUrl;
  const mainAr = mainNormW / mainNormH;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) {
          resolve(resultDataUrl);
          return;
        }

        let sx = 0;
        let sy = 0;
        let sw = w;
        let sh = h;

        let bestN = 0;
        let bestScore = Infinity;
        for (let n = 2; n <= 8; n++) {
          const pw = w / n;
          const panelAr = pw / h;
          const arErr = Math.abs(panelAr - mainAr) / mainAr;
          const wErr = Math.abs(pw - mainNormW) / Math.max(1, mainNormW);
          const hErr = Math.abs(h - mainNormH) / Math.max(1, mainNormH);
          // 单列宽高比应接近主图；列宽约等于提交 RunningHub 的主图宽；整图高度接近主图高
          if (arErr < 0.11 && wErr < 0.18 && hErr < 0.14) {
            const score = arErr + wErr + hErr * 0.5;
            if (score < bestScore) {
              bestScore = score;
              bestN = n;
            }
          }
        }

        if (bestN >= 2) {
          const pw = Math.floor(w / bestN);
          const idx = pickToneResultPanelIndex(bestN);
          sx = Math.min(idx * pw, Math.max(0, w - pw));
          sy = 0;
          sw = pw;
          sh = h;
        }

        const out = coverRegionToDataUrl(img, sx, sy, sw, sh, mainNormW, mainNormH, 0.92);
        resolve(out || resultDataUrl);
      } catch {
        resolve(resultDataUrl);
      }
    };
    img.onerror = () => resolve(resultDataUrl);
    img.src = resultDataUrl;
  });
}

/** 左侧栏：上图下文，固定高度槽位（支持拖入图片） */
const SidebarImageSlot: React.FC<{
  title: string;
  src?: string | null;
  onAction: () => void;
  /** 从资源管理器拖入图片时回调（与点击选图一致） */
  onDropFile?: (file: File) => void;
  isDarkMode: boolean;
}> = ({ title, src, onAction, onDropFile, isDarkMode }) => {
  const [dragOver, setDragOver] = useState(false);
  const canDrop = !!onDropFile;

  const onDragOver = (e: React.DragEvent) => {
    if (!canDrop) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const onDragEnter = (e: React.DragEvent) => {
    if (!canDrop) return;
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!canDrop) return;
    const related = e.relatedTarget as Node | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    setDragOver(false);
  };
  const onDrop = (e: React.DragEvent) => {
    if (!canDrop) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = Array.from(e.dataTransfer.files).find((x) => x.type.startsWith('image/'));
    if (f) onDropFile(f);
  };

  const dropRing =
    dragOver && canDrop
      ? 'border-[#D7CDA1] border-2 ring-2 ring-[#D7CDA1]/40'
      : isDarkMode
        ? 'border-white/10 hover:border-[#D7CDA1]/50'
        : 'border-zinc-200 hover:border-[#D7CDA1]/60';

  return (
    <div className="flex flex-col gap-2 shrink-0">
      <div
        className={`relative w-full aspect-[3/4] rounded-xl border flex items-center justify-center cursor-pointer transition-colors group overflow-hidden ${dropRing} ${
          isDarkMode ? 'bg-black/20' : 'bg-white'
        }`}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {src ? (
          <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
        ) : null}
        <button
          type="button"
          onClick={onAction}
          onDragOver={onDragOver}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`relative z-10 w-[72px] h-[72px] rounded-lg flex items-center justify-center transition-colors ${
            isDarkMode ? 'bg-white/10 group-hover:bg-white/20' : 'bg-zinc-100 group-hover:bg-zinc-200'
          }`}
        >
          <svg className={`w-10 h-10 ${isDarkMode ? 'text-white/60' : 'text-zinc-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m-7-7h14" />
          </svg>
        </button>
      </div>
      <div className="mt-2 flex justify-center">
        <span
          className="text-sm leading-none font-bold tracking-wider text-[#101820] bg-[#D7CDA1] text-center px-3 py-1"
          style={{ borderRadius: '5px' }}
        >
          {title}
        </span>
      </div>
    </div>
  );
};

const ToneMappingModal: React.FC<ToneMappingModalProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  language,
  userEmail,
  isAuthenticated = false,
  recoveryRequest = null,
}) => {
  const tK = (key: Parameters<typeof tI18n>[0]) => tI18n(key, language);
  const zh = language !== 'en';
  const { showToast } = useToast();
  const shellBg = isDarkMode ? 'bg-black text-white' : 'bg-zinc-50 text-zinc-900';
  const headerBg = isDarkMode ? 'bg-black/80 border-white/10' : 'bg-white/80 border-zinc-200';
  const asideBg = isDarkMode ? 'bg-black/60 border-white/10' : 'bg-white border-zinc-200';
  const btnGhost = isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-zinc-200 hover:bg-zinc-300 text-zinc-900';
  const textMuted = isDarkMode ? 'text-white/60' : 'text-zinc-600';
  const textFaint = isDarkMode ? 'text-white/50' : 'text-zinc-500';
  
  // 积分扣费 Hook
  const { checkBalance, isDeducting: isDeductingCredits } = useCreditDeduction({
    language: zh ? 'zh' : 'en',
    onInsufficientBalance: (required, available) => {
      alert(zh 
        ? `积分不足：需要 ${required} 积分，当前 ${available} 积分，请及时充值` 
        : `Insufficient credits: need ${required}, have ${available}. Please recharge.`
      );
    },
    onDeductionError: (error) => {
      alert(zh ? `扣费失败：${error}` : `Credit deduction failed: ${error}`);
    }
  });

  const mainInputRef = useRef<HTMLInputElement>(null);
  const shadowInputRef = useRef<HTMLInputElement>(null);
  const compareContainerRef = useRef<HTMLDivElement>(null);
  /** 缩放/平移层内、用于量可用区域的居中舞台（不受对比框尺寸影响） */
  const compareStageRef = useRef<HTMLDivElement>(null);
  const compareInnerRef = useRef<HTMLDivElement>(null);
  /** 铺满底层的对比图（生成结果），用于滑杆量宽与平移边界 */
  const baseImgRef = useRef<HTMLImageElement>(null);
  /** 缩放拖动画布时用 ref 保存最新 pan，避免 effect 依赖 panX/Y 导致监听反复拆除 */
  const panLatestRef = useRef({ panX: 0, panY: 0 });
  const panDragRef = useRef<{
    active: boolean;
    startClientX: number;
    startClientY: number;
    startPanX: number;
    startPanY: number;
  } | null>(null);
  const zoomLevelPinchRef = useRef(1);

  const [mainImage, setMainImage] = useState<string | null>(null);
  const [shadowImage, setShadowImage] = useState<string | null>(null);
  /** 预览唯一真相源：before/after 均来自此项（禁止 resultImage / selectedHistoryId 双轨） */
  const [currentToneMappingItem, setCurrentToneMappingItem] = useState<ToneHistoryItem | null>(null);
  const historySelectSeqRef = useRef(0);
  /** 已裁切 resultSnapshot（按 id），refresh 重绑定时保留，避免重复裁切/闪屏 */
  const croppedResultByIdRef = useRef<Map<string, string>>(new Map());
  const p3ClickedIdRef = useRef<string | null>(null);
  const toneHistoryP3Ref = useRef<ToneHistoryItem[]>([]);
  const currentP3Ref = useRef<ToneHistoryItem | null>(null);
  const compareBaseP3Ref = useRef<string | null>(null);
  const compareAfterP3Ref = useRef<string | null>(null);

  const p3Trace = useCallback(
    (
      action: string,
      overrides?: Partial<{
        clickedId: string | null;
        currentId: string | null;
        current: ToneHistoryItem | null;
        compareBase: string | null;
        compareAfter: string | null;
        cacheLookupId: string | null;
        clickedItemMainSnapshot80: string;
        clickedItemResultSnapshot80: string;
        cropSeq: number;
        targetItemId: string | null;
        croppedResult80: string;
        seqDiscarded: boolean;
        outputBefore80: string;
        outputAfter80: string;
        stateBefore80: string;
        stateAfter80: string;
        extra: Record<string, unknown>;
      }>,
    ) => {
      const current = overrides?.current !== undefined ? overrides.current : currentP3Ref.current;
      toneMappingP3Trace({
        action,
        clickedId: overrides?.clickedId !== undefined ? overrides.clickedId : p3ClickedIdRef.current,
        currentId: overrides?.currentId !== undefined ? overrides.currentId : current?.id ?? null,
        current,
        compareBase: overrides?.compareBase !== undefined ? overrides.compareBase : compareBaseP3Ref.current,
        compareAfter: overrides?.compareAfter !== undefined ? overrides.compareAfter : compareAfterP3Ref.current,
        toneHistoryIds: toneHistoryP3Ref.current.map((h) => h.id),
        croppedResultById: croppedResultByIdRef.current,
        cacheLookupId: overrides?.cacheLookupId,
        clickedItemMainSnapshot80: overrides?.clickedItemMainSnapshot80,
        clickedItemResultSnapshot80: overrides?.clickedItemResultSnapshot80,
        cropSeq: overrides?.cropSeq,
        targetItemId: overrides?.targetItemId,
        croppedResult80: overrides?.croppedResult80,
        seqDiscarded: overrides?.seqDiscarded,
        outputBefore80: overrides?.outputBefore80,
        outputAfter80: overrides?.outputAfter80,
        stateBefore80: overrides?.stateBefore80,
        stateAfter80: overrides?.stateAfter80,
        extra: overrides?.extra,
      });
    },
    [],
  );
  const [isGenerating, setIsGenerating] = useState(false);
  /** 请求未完成前不允许再次生成（关面板也不重置） */
  const toneGenerationInFlightRef = useRef(false);
  const [progressDesc, setProgressDesc] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [comparePos, setComparePos] = useState(50);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);

  const [toneHistory, setToneHistory] = useState<ToneHistoryItem[]>([]);

  const [imageBounds, setImageBounds] = useState<{ left: number; width: number } | null>(null);
  const [isDraggingSlider, setIsDraggingSlider] = useState(false);
  const runningHubLinked = useRunningHubLinkIndicator(isOpen);
  /** 以底图（对比左侧）原始尺寸为准，对比区画幅与侧栏竖图/横图一致 */
  const [compareNaturalWH, setCompareNaturalWH] = useState<{ w: number; h: number } | null>(null);
  const [fittedCompareFrame, setFittedCompareFrame] = useState<{ w: number; h: number } | null>(null);
  /**
   * 对比区专用：底图 + 结果在 canvas 中先对齐像素尺寸，再统一缩放到 fitted 框像素。
   * 避免只靠 CSS object-cover 时两图源宽高比不同导致无法像素对齐。
   */
  const [compareDisplayPair, setCompareDisplayPair] = useState<{ base: string; result: string } | null>(null);

  /**
   * RunningHub 外链图在浏览器 canvas 上处理常被 CORS 污染，导致裁切失败→回退原图（于是你看到还是横图）。
   * 这里把 http(s) 图片先经后端转成 dataURL（同源），再做裁切，保证一定能强制匹配主图画幅。
   */
  const ensureCanvasSafeDataUrl = useCallback(async (src: string) => {
    const s = String(src || '').trim();
    if (!s) return '';
    if (!/^https?:\/\//i.test(s)) return s;
    try {
      const resp = await api.fetchImageDataUrl(s);
      const dataUrl = (resp as any)?.dataUrl as string | undefined;
      if (dataUrl && /^data:image\//i.test(dataUrl)) return dataUrl;
      return s;
    } catch {
      return s;
    }
  }, []);

  const isToneMappingTestMode = import.meta.env.VITE_TONE_MAPPING_TEST_MODE === 'true';
  const canGenerate = !!mainImage && !!shadowImage && !isGenerating && (isToneMappingTestMode || !!userEmail?.trim());

  /**
   * 侧栏：上格 shadowImage；下格 mainImage（生成输入槽，不参与对比 fallback）。
   * before/after 仅读 currentToneMappingItem 原子配对。
   */
  const compareBase = useMemo(
    () => toneMappingCompareBefore(currentToneMappingItem),
    [currentToneMappingItem],
  );

  const compareAfter = useMemo(
    () => toneMappingCompareAfter(currentToneMappingItem),
    [currentToneMappingItem],
  );

  const compareSliderKey = useMemo(
    () => toneMappingCompareSliderKey(currentToneMappingItem, compareBase, compareAfter),
    [currentToneMappingItem, compareBase, compareAfter],
  );

  const showCompare = !!(
    compareBase &&
    compareAfter &&
    compareAfter !== compareBase
  );

  useEffect(() => {
    toneHistoryP3Ref.current = toneHistory;
    pruneToneMappingCroppedResultCache(
      croppedResultByIdRef.current,
      toneHistory.map((h) => h.id),
    );
  }, [toneHistory]);

  useEffect(() => {
    compareBaseP3Ref.current = compareBase;
    compareAfterP3Ref.current = compareAfter;
    p3Trace('compare_derived', {
      currentId: currentToneMappingItem?.id ?? null,
      current: currentToneMappingItem,
      compareBase,
      compareAfter,
    });
  }, [compareBase, compareAfter, currentToneMappingItem, p3Trace]);

  useEffect(() => {
    currentP3Ref.current = currentToneMappingItem;
    p3Trace('current_changed', {
      currentId: currentToneMappingItem?.id ?? null,
      current: currentToneMappingItem,
    });
  }, [currentToneMappingItem, p3Trace]);

  useEffect(() => {
    if (!isOpen) return;
    p3Trace('toneHistory_refresh', { extra: { count: toneHistory.length } });
  }, [toneHistory, isOpen, p3Trace]);

  useEffect(() => {
    if (!compareDisplayPair) return;
    p3Trace('compareDisplayPair_state', {
      currentId: currentP3Ref.current?.id ?? null,
      stateBefore80: toneMappingSnip80(compareDisplayPair.base),
      stateAfter80: toneMappingSnip80(compareDisplayPair.result),
      extra: {
        pairBase80: toneMappingSnip80(compareDisplayPair.base),
        pairResult80: toneMappingSnip80(compareDisplayPair.result),
      },
    });
  }, [compareDisplayPair, p3Trace]);

  useEffect(() => {
    p3Trace(isOpen ? 'panel_open' : 'panel_close', {
      extra: { generationInFlight: toneGenerationInFlightRef.current },
    });
  }, [isOpen, p3Trace]);

  useRunningHubPanelHistory({
    featureType: 'tone_mapping',
    isOpen,
    isAuthenticated: !!isAuthenticated,
    userEmail,
    language: zh ? 'zh' : 'en',
    showToast,
    setHistory: setToneHistory,
    cleanExpired: cleanExpiredToneHistory,
    onBeforeClear: () => {
      historySelectSeqRef.current += 1;
      croppedResultByIdRef.current.clear();
      setCurrentToneMappingItem(null);
    },
    mapLoadedItems: (items) =>
      persistToneHistoryItems(items.slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS), userEmail),
  });

  useEffect(() => {
    if (isOpen && toneGenerationInFlightRef.current) {
      setIsGenerating(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      if (!toneGenerationInFlightRef.current) {
        historySelectSeqRef.current += 1;
        croppedResultByIdRef.current.clear();
        setCurrentToneMappingItem(null);
        setZoomLevel(1);
        setPanX(0);
        setPanY(0);
        setIsPanning(false);
        setCompareNaturalWH(null);
        setFittedCompareFrame(null);
        setCompareDisplayPair(null);
        setComparePos(50);
        setImageBounds(null);
      }
    }
  }, [isOpen]);

  /** 云端/本地历史刷新后：id 缺失则清空；存在则 merge(fresh, current) 重绑定（保留裁切缓存） */
  useEffect(() => {
    if (!isOpen) return;
    const current = currentToneMappingItem;
    const id = current?.id;
    if (!id) return;

    const fresh = toneHistory.find((h) => h.id === id);
    if (!fresh) {
      historySelectSeqRef.current += 1;
      croppedResultByIdRef.current.delete(id);
      setCurrentToneMappingItem(null);
      setComparePos(50);
      setCompareNaturalWH(null);
      setFittedCompareFrame(null);
      setCompareDisplayPair(null);
      setImageBounds(null);
      p3Trace('toneHistory_rebind_clear', { current, cacheLookupId: id });
      return;
    }

    const merged = mergeToneMappingCurrentFromHistory(
      fresh,
      current,
      croppedResultByIdRef.current,
    );
    if (toneMappingPreviewFieldsEqual(current, merged)) return;
    p3Trace('toneHistory_rebind_merge', {
      current,
      cacheLookupId: id,
      extra: {
        mergedResult80: toneMappingSnip80(merged.resultSnapshot),
        freshResult80: toneMappingSnip80(fresh.resultSnapshot),
      },
    });
    setCurrentToneMappingItem(merged);
  }, [isOpen, toneHistory, currentToneMappingItem]);

  useEffect(() => {
    if (!showCompare || !compareBase) {
      setCompareNaturalWH(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const w = img.naturalWidth || img.width || 1;
      const h = img.naturalHeight || img.height || 1;
      setCompareNaturalWH({ w, h });
    };
    img.onerror = () => {
      if (!cancelled) setCompareNaturalWH(null);
    };
    img.src = compareBase;
    return () => {
      cancelled = true;
    };
  }, [showCompare, compareBase]);

  /** 双图统一光栅到 fitted 尺寸，保证分割线两侧同一几何尺度（不依赖浏览器对两图源分别算 cover） */
  useEffect(() => {
    if (!showCompare || !compareBase || !compareAfter || !compareNaturalWH || !fittedCompareFrame) {
      setCompareDisplayPair(null);
      return;
    }
    let cancelled = false;
    const { w: nw, h: nh } = compareNaturalWH;
    const { w: fw, h: fh } = fittedCompareFrame;
    const pairStartCurrentId = currentP3Ref.current?.id ?? null;
    const pairStartCompareBase80 = toneMappingSnip80(compareBase);
    const pairStartCompareAfter80 = toneMappingSnip80(compareAfter);

    p3Trace('compareDisplayPair_start', {
      currentId: pairStartCurrentId,
      compareBase,
      compareAfter,
      extra: {
        naturalW: nw,
        naturalH: nh,
        fittedW: fw,
        fittedH: fh,
        startCurrentId: pairStartCurrentId,
        startCompareBase80: pairStartCompareBase80,
        startCompareAfter80: pairStartCompareAfter80,
      },
    });

    const loadImg = (src: string) =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const tryOne = (useCors: boolean) => {
          const img = new Image();
          if (useCors) img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = () => {
            if (useCors) tryOne(false);
            else reject(new Error('compare image load failed'));
          };
          img.src = src;
        };
        tryOne(true);
      });

    void (async () => {
      try {
        const [baseSafe, resSafe] = await Promise.all([
          ensureCanvasSafeDataUrl(compareBase),
          ensureCanvasSafeDataUrl(compareAfter),
        ]);
        if (cancelled) return;
        const [baseImg, resImg] = await Promise.all([loadImg(baseSafe), loadImg(resSafe)]);
        if (cancelled) return;

        // 结果对齐到主图「逻辑画幅」nw×nh：宽高比一致时均匀缩放；不一致时再 cover（居中裁切）。
        const riw = resImg.naturalWidth || resImg.width;
        const rih = resImg.naturalHeight || resImg.height;
        const mainAr = nw / nh;
        const resAr = riw && rih ? riw / rih : mainAr;
        const normUrl =
          riw && rih && Math.abs(mainAr - resAr) <= COMPARE_ASPECT_EPS
            ? resizeImageToCompareBoxDataUrl(resImg, nw, nh)
            : coverImageToDataUrl(resImg, nw, nh);
        let resAligned = resImg;
        if (normUrl) {
          try {
            resAligned = await loadImg(normUrl);
          } catch {
            resAligned = resImg;
          }
        }
        if (cancelled) return;

        // 对比区：两侧同一 dw×dh，优先均匀缩放（与 fitted 框宽高比一致），避免 CSS object-cover 各算各的裁切。
        const baseTile = resizeImageToCompareBoxDataUrl(baseImg, fw, fh);
        const resultTile = resizeImageToCompareBoxDataUrl(resAligned, fw, fh);
        if (cancelled) return;

        if (baseTile && resultTile) {
          const outputBefore80 = toneMappingSnip80(baseTile);
          const outputAfter80 = toneMappingSnip80(resultTile);
          p3Trace('compareDisplayPair_done', {
            currentId: currentP3Ref.current?.id ?? null,
            compareBase,
            compareAfter,
            outputBefore80,
            outputAfter80,
            extra: {
              startCurrentId: pairStartCurrentId,
              doneCurrentId: currentP3Ref.current?.id ?? null,
              startCompareBase80: pairStartCompareBase80,
              startCompareAfter80: pairStartCompareAfter80,
              doneCompareBase80: toneMappingSnip80(compareBase),
              doneCompareAfter80: toneMappingSnip80(compareAfter),
              currentIdDrift: pairStartCurrentId !== (currentP3Ref.current?.id ?? null),
              compareBaseDrift: pairStartCompareBase80 !== toneMappingSnip80(compareBase),
              compareAfterDrift: pairStartCompareAfter80 !== toneMappingSnip80(compareAfter),
              cancelled,
            },
          });
          setCompareDisplayPair({ base: baseTile, result: resultTile });
        } else if (!cancelled) {
          p3Trace('compareDisplayPair_done_null', { compareBase, compareAfter });
          setCompareDisplayPair(null);
        }
      } catch {
        if (!cancelled) {
          p3Trace('compareDisplayPair_error', { compareBase, compareAfter });
          setCompareDisplayPair(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showCompare, compareBase, compareAfter, compareNaturalWH, fittedCompareFrame, ensureCanvasSafeDataUrl, p3Trace]);

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
      r.onerror = () => reject(r.error ?? new Error('read failed'));
      r.readAsDataURL(file);
    });

  const applyMainFile = async (file: File) => {
    if (!isSupportedUploadImageFile(file)) {
      showToast(getUnsupportedUploadImageFormatMessage(zh ? 'zh' : 'en'), 'warning');
      return;
    }
    const dataUrl = await readFileAsDataUrl(file);
    setMainImage(dataUrl || null);
    historySelectSeqRef.current += 1;
    setCurrentToneMappingItem(null);
  };

  const applyShadowFile = async (file: File) => {
    if (!isSupportedUploadImageFile(file)) {
      showToast(getUnsupportedUploadImageFormatMessage(zh ? 'zh' : 'en'), 'warning');
      return;
    }
    const dataUrl = await readFileAsDataUrl(file);
    setShadowImage(dataUrl || null);
    historySelectSeqRef.current += 1;
    setCurrentToneMappingItem(null);
  };

  const ensureMax2048DataUrl = (src: string): Promise<{ dataUrl: string; width: number; height: number }> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let w = img.naturalWidth || img.width || 1;
        let h = img.naturalHeight || img.height || 1;
        const max = Math.max(w, h);
        let tw = w;
        let th = h;
        if (max > 2048) {
          const s = 2048 / max;
          tw = Math.max(1, Math.round(w * s));
          th = Math.max(1, Math.round(h * s));
        }
        const c = document.createElement('canvas');
        c.width = tw;
        c.height = th;
        const ctx = c.getContext('2d');
        if (!ctx) return reject(new Error('Canvas unsupported'));
        ctx.drawImage(img, 0, 0, tw, th);
        resolve({ dataUrl: c.toDataURL('image/jpeg', 0.92), width: tw, height: th });
      };
      img.onerror = () => reject(new Error('无法读取图片'));
      img.src = src;
    });

  const onPickMain = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    await applyMainFile(f);
  };

  const onPickShadow = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    await applyShadowFile(f);
  };

  const pushHistory = useCallback(
    (
      mainSnap: string,
      shadowSnap: string,
      resultSnap: string,
      scenePanelWidthPx?: number,
      scenePanelHeightPx?: number,
      opts?: { skipCloudSync?: boolean; id?: string },
    ): ToneHistoryItem | null => {
      const newId =
        opts?.id?.trim() ||
        createScopedRunningHubClientItemId('tone_mapping', userEmail, !!isAuthenticated);
      const stamped = stampRunningHubHistoryOwner(
        {
          id: newId,
          mainSnapshot: mainSnap,
          shadowSnapshot: shadowSnap,
          resultSnapshot: resultSnap,
          ...(typeof scenePanelWidthPx === 'number' && scenePanelWidthPx > 0 ? { scenePanelWidthPx } : {}),
          ...(typeof scenePanelHeightPx === 'number' && scenePanelHeightPx > 0 ? { scenePanelHeightPx } : {}),
          timestamp: Date.now(),
        },
        userEmail,
      );
      if (!stamped) return null;
      const newItem = stamped as ToneHistoryItem;
      setToneHistory((prev) => {
        const cleaned = cleanExpiredToneHistory(prev);
        const next = [newItem, ...cleaned.filter((x) => x.id !== newId)].slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS);
        return persistToneHistoryItems(next, userEmail);
      });
      if (!opts?.skipCloudSync) {
        void (async () => {
          const r = await syncRunningHubHistoryItemToCloud('tone_mapping', !!isAuthenticated, newItem.id, newItem, {
            userEmailForLog: userEmail,
            currentUserEmail: userEmail,
          });
          if (!r.ok && !r.skipped) {
            showToast(zh ? `云端存档失败：${r.error || ''}` : `Cloud archive failed: ${r.error || ''}`, 'error');
          }
          if (r.thumbUrl && isRunningHubListThumbUrl(r.thumbUrl)) {
            setToneHistory((prev) =>
              persistToneHistoryItems(
                prev.map((it) => (it.id === newId ? { ...it, thumbUrl: r.thumbUrl } : it)),
                userEmail,
              ),
            );
            setCurrentToneMappingItem((cur) => {
              if (cur?.id !== newId) return cur;
              return buildToneMappingCurrentFromItem(
                { ...cur, thumbUrl: r.thumbUrl },
                croppedResultByIdRef.current,
              );
            });
          }
        })();
      }
      return newItem;
    },
    [isAuthenticated, userEmail, zh, showToast],
  );

  const runGenerate = async () => {
    if (!mainImage || !shadowImage || isGenerating || toneGenerationInFlightRef.current) return;
    if (!isSupportedUploadImageDataUrl(mainImage) || !isSupportedUploadImageDataUrl(shadowImage)) {
      showToast(getUnsupportedUploadImageFormatMessage(zh ? 'zh' : 'en'), 'warning');
      return;
    }
    const email = userEmail?.trim() || (isToneMappingTestMode ? 'tone-map-test@local.dev' : '');
    if (!email) {
      alert(zh ? '请先登录后再使用色调映射（需扣积分）。' : 'Please sign in to use Tone Mapping (credits required).');
      return;
    }

    toneGenerationInFlightRef.current = true;
    historySelectSeqRef.current += 1;
    setIsGenerating(true);
    setProgressDesc(zh ? '正在提交任务…' : 'Submitting…');
    setProgressPercent(10);
    let progressInterval: number | undefined;
    try {
      // 仅预检：扣费在 /api/gemini/render（RunningHub 色调映射），避免与前端 deduct 双扣
      if (!isToneMappingTestMode) {
        const { sufficient, required, available } = await checkBalance('tone_mapping');
        if (!sufficient) {
          alert(
            zh
              ? `积分不足：色调映射需要 ${required} 积分，当前 ${available} 积分`
              : `Insufficient credits: need ${required}, have ${available}.`,
          );
          return;
        }
      }

      progressInterval = window.setInterval(() => {
        setProgressPercent((p) => {
          if (p >= 90) return p;
          const next = p + Math.random() * 6 + 2;
          return Math.min(90, Math.round(next));
        });
        setProgressDesc(zh ? '正在生成…' : 'Generating…');
      }, 900);

      // 约定：
      // - shadowImage（载入光影）= 风格/光影参考图
      // - mainImage（载入主图）= 需要被映射的目标图（对比左侧底图）
      // 重要：后端 tone-map 入口将 body.image 作为主图输入；shadowImageDataUrl 为第二路参考图。
      // 为避免“看起来像高清放大”的混淆，这里显式按「主图=main，参考=shadow」传参。
      const norm = await ensureMax2048DataUrl(mainImage);
      const clientItemId =
        isAuthenticated && userEmail?.trim()
          ? createRunningHubClientItemId('tone_mapping', userEmail, true)
          : '';
      const jobMeta =
        clientItemId ?
          {
            clientItemId,
            historyPayloadDraft: {
              id: clientItemId,
              mainSnapshot: mainImage,
              shadowSnapshot: shadowImage,
              timestamp: Date.now(),
              scenePanelWidthPx: norm.width,
              scenePanelHeightPx: norm.height,
            },
          }
        : undefined;
      setProgressPercent(35);
      const palette = await extractPaletteFromImageUrls([shadowImage], 5);
      setProgressPercent(55);
      const heur = analyzePaletteHeuristic(palette);
      const dnaSummary = `${heur.summaryZh}\n${heur.blueCompositionZh}`;
      setProgressPercent(70);
      const res = await renderToneMappingUniversalPbr(
        norm.dataUrl,
        palette,
        dnaSummary,
        { width: norm.width, height: norm.height },
        email,
        shadowImage,
        jobMeta,
      );
      setProgressPercent(88);
      const rawOut = ((res as { image?: string; url?: string }).image || (res as { url?: string }).url || '').trim();
      if (!rawOut) throw new Error(zh ? '服务未返回图片地址' : 'No image URL in response');
      const safeRaw = await ensureCanvasSafeDataUrl(rawOut);
      const resultSingle = await cropToneMappingResultToSinglePanel(safeRaw, norm.width, norm.height);
      const mainStored = await compressImageSrcForToneHistorySlot(mainImage, ensureCanvasSafeDataUrl);
      const shadowStored = await compressImageSrcForToneHistorySlot(shadowImage, ensureCanvasSafeDataUrl);
      const newItem = pushHistory(mainStored, shadowStored, resultSingle, norm.width, norm.height, {
        skipCloudSync: !!res.historyArchived,
        id: clientItemId || undefined,
      });
      if (newItem) {
        croppedResultByIdRef.current.set(newItem.id, resultSingle);
        historySelectSeqRef.current += 1;
        setCurrentToneMappingItem(
          buildToneMappingCurrentFromItem(newItem, croppedResultByIdRef.current),
        );
        setComparePos(50);
        setZoomLevel(1);
        setPanX(0);
        setPanY(0);
        setCompareNaturalWH(null);
        setFittedCompareFrame(null);
        setCompareDisplayPair(null);
        setImageBounds(null);
      }
      setProgressPercent(100);
      setProgressDesc(zh ? '生成完成' : 'Done');
    } catch (err: any) {
      alert(err?.message || String(err));
    } finally {
      if (progressInterval) window.clearInterval(progressInterval);
      window.setTimeout(() => {
        setProgressDesc('');
        setProgressPercent(0);
      }, 800);
      toneGenerationInFlightRef.current = false;
      setIsGenerating(false);
    }
  };

  const selectHistoryItem = async (item: ToneHistoryItem) => {
    const seq = ++historySelectSeqRef.current;
    p3ClickedIdRef.current = item.id;
    p3Trace('selectHistoryItem_start', {
      clickedId: item.id,
      currentId: currentP3Ref.current?.id ?? null,
      current: currentP3Ref.current,
      cacheLookupId: item.id,
      clickedItemMainSnapshot80: toneMappingSnip80(item.mainSnapshot),
      clickedItemResultSnapshot80: toneMappingSnip80(item.resultSnapshot),
      cropSeq: seq,
      targetItemId: item.id,
      extra: { seq },
    });
    setComparePos(50);
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
    setCompareNaturalWH(null);
    setFittedCompareFrame(null);
    setCompareDisplayPair(null);
    setImageBounds(null);

    const mainSnap = item.mainSnapshot?.trim() || '';
    const shadowSnap = item.shadowSnapshot?.trim() || '';
    setMainImage(mainSnap || null);
    setShadowImage(shadowSnap || null);

    const resSnap = item.resultSnapshot?.trim() || '';
    setCurrentToneMappingItem(
      buildToneMappingCurrentFromItem(
        {
          id: item.id,
          mainSnapshot: mainSnap,
          shadowSnapshot: shadowSnap,
          resultSnapshot: resSnap,
          thumbUrl: item.thumbUrl,
          scenePanelWidthPx: item.scenePanelWidthPx,
          scenePanelHeightPx: item.scenePanelHeightPx,
          timestamp: item.timestamp,
          ownerEmail: item.ownerEmail,
        },
        croppedResultByIdRef.current,
      ),
    );

    const cachedCrop = getToneMappingCroppedResultForId(item.id, croppedResultByIdRef.current);

    if (!mainSnap || !shadowSnap) {
      alert(
        zh
          ? !mainSnap && !shadowSnap
            ? '该条历史未保存当时的「加载主图 / 加载光影」（多为旧版本或云端精简同步）。请重新载入两张图后再生成。'
            : !shadowSnap
              ? '该条历史未保存当时的「加载光影」参考图（旧数据或云端体积限制）。请手动补一张光影图后再生成。'
              : '该条历史未保存当时的「加载主图」（旧数据或云端体积限制）。请手动补一张主图后再生成。'
          : !mainSnap && !shadowSnap
            ? 'This history entry has no saved main / lighting inputs (older app version or cloud slim sync). Load both images again to regenerate.'
            : !shadowSnap
              ? 'No lighting reference was saved for this entry. Please load a lighting image before regenerating.'
              : 'No main image was saved for this entry. Please load a main image before regenerating.',
      );
    }

    if (!resSnap) return;
    if (cachedCrop) return;

    let w = item.scenePanelWidthPx ?? 0;
    let h = item.scenePanelHeightPx ?? 0;
    const szFull = mainSnap ? await readImageNaturalSize(mainSnap) : { w: 0, h: 0 };
    if (seq !== historySelectSeqRef.current) {
      p3Trace('crop_seq_discarded', {
        cropSeq: seq,
        targetItemId: item.id,
        clickedId: item.id,
        currentId: currentP3Ref.current?.id ?? null,
        seqDiscarded: true,
        extra: {
          latestSeq: historySelectSeqRef.current,
          discardAfter: 'readImageNaturalSize',
        },
      });
      return;
    }

    if (w > 0 && h > 0) {
      /* 已存齐 */
    } else if (w > 0 && szFull.w > 0 && szFull.h > 0) {
      h = Math.max(1, Math.round((w * szFull.h) / szFull.w));
    } else if (h > 0 && szFull.w > 0 && szFull.h > 0) {
      w = Math.max(1, Math.round((h * szFull.w) / szFull.h));
    } else {
      w = szFull.w;
      h = szFull.h;
    }

    let displayResult = resSnap;
    if (w > 0 && h > 0) {
      const safeRes = /^https?:\/\//i.test(resSnap) ? await ensureCanvasSafeDataUrl(resSnap) : resSnap;
      if (seq !== historySelectSeqRef.current) {
        p3Trace('crop_seq_discarded', {
          cropSeq: seq,
          targetItemId: item.id,
          clickedId: item.id,
          currentId: currentP3Ref.current?.id ?? null,
          seqDiscarded: true,
          extra: {
            latestSeq: historySelectSeqRef.current,
            discardAfter: 'ensureCanvasSafeDataUrl',
          },
        });
        return;
      }
      displayResult = await cropToneMappingResultToSinglePanel(safeRes, w, h);
    }
    if (seq !== historySelectSeqRef.current) {
      p3Trace('crop_seq_discarded', {
        cropSeq: seq,
        targetItemId: item.id,
        clickedId: item.id,
        currentId: currentP3Ref.current?.id ?? null,
        seqDiscarded: true,
        extra: {
          latestSeq: historySelectSeqRef.current,
          discardAfter: 'cropToneMappingResultToSinglePanel',
        },
      });
      return;
    }

    croppedResultByIdRef.current.set(item.id, displayResult);
    p3Trace('crop_before_write_current', {
      cropSeq: seq,
      clickedId: item.id,
      currentId: currentP3Ref.current?.id ?? null,
      targetItemId: item.id,
      croppedResult80: toneMappingSnip80(displayResult),
      seqDiscarded: false,
      current: currentP3Ref.current,
      cacheLookupId: item.id,
      extra: { latestSeq: historySelectSeqRef.current },
    });
    setCurrentToneMappingItem((prev) => {
      if (prev?.id !== item.id) return prev;
      return buildToneMappingCurrentFromItem(
        {
          id: item.id,
          mainSnapshot: mainSnap,
          shadowSnapshot: shadowSnap,
          resultSnapshot: resSnap,
          thumbUrl: item.thumbUrl,
          scenePanelWidthPx: item.scenePanelWidthPx,
          scenePanelHeightPx: item.scenePanelHeightPx,
          timestamp: item.timestamp,
          ownerEmail: item.ownerEmail,
        },
        croppedResultByIdRef.current,
      );
    });
  };

  useRunningHubPanelRecovery({
    featureType: 'tone_mapping',
    recoveryRequest,
    mapPayloadToItem: (p) => {
      const resultSnapshot = typeof p.resultSnapshot === 'string' ? p.resultSnapshot.trim() : '';
      if (!resultSnapshot) return null;
      const mainSnapshot = typeof p.mainSnapshot === 'string' ? p.mainSnapshot : '';
      const shadowSnapshot = typeof p.shadowSnapshot === 'string' ? p.shadowSnapshot : '';
      return {
        id: String(
          p.id || createScopedRunningHubClientItemId('tone_mapping', userEmail, !!isAuthenticated),
        ),
        mainSnapshot,
        shadowSnapshot,
        resultSnapshot,
        timestamp: Number(p.timestamp || Date.now()),
        ...(typeof p.scenePanelWidthPx === 'number' && p.scenePanelWidthPx > 0 ? { scenePanelWidthPx: p.scenePanelWidthPx } : {}),
        ...(typeof p.scenePanelHeightPx === 'number' && p.scenePanelHeightPx > 0 ? { scenePanelHeightPx: p.scenePanelHeightPx } : {}),
        ...(typeof p.thumbUrl === 'string' && isRunningHubListThumbUrl(p.thumbUrl) ? { thumbUrl: p.thumbUrl } : {}),
      };
    },
    mergeHistory: (item) => {
      pushHistory(item.mainSnapshot, item.shadowSnapshot || '', item.resultSnapshot, item.scenePanelWidthPx, item.scenePanelHeightPx, {
        skipCloudSync: true,
        id: item.id,
      });
    },
    applyItem: (item) => {
      void selectHistoryItem(item);
    },
  });

  const deleteHistoryItem = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm(zh ? '确定删除这条生成记录？' : 'Delete this history entry?')) return;
    void deleteRunningHubHistoryFromCloud('tone_mapping', !!isAuthenticated, id);
    const nextList = toneHistory.filter((x) => x.id !== id);
    setToneHistory(persistToneHistoryItems(nextList, userEmail));
    croppedResultByIdRef.current.delete(id);
    if (currentToneMappingItem?.id === id) {
      historySelectSeqRef.current += 1;
      const replacement = pickToneMappingCurrentAfterDelete(toneHistory, id);
      if (replacement && replacement.id !== id) {
        void selectHistoryItem(replacement);
      } else {
        setCurrentToneMappingItem(null);
        setComparePos(50);
        setCompareNaturalWH(null);
        setFittedCompareFrame(null);
        setCompareDisplayPair(null);
        setImageBounds(null);
      }
    }
  };

  const downloadToneHistoryItem = async (e: React.MouseEvent, item: ToneHistoryItem) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const watermarkedImage = await addWatermarkToImage(item.resultSnapshot);
      const link = document.createElement('a');
      link.href = watermarkedImage;
      link.download = generatedImageDownloadFilename(item.timestamp);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);
    } catch (err) {
      console.error('[ToneMapping] download failed:', err);
      alert(
        zh
          ? '下载失败，请登录账号后重试（云端图片需后端拉取才能加水印）。'
          : 'Download failed. Sign in and retry if the image is from cloud storage.',
      );
    }
  };

  const zoomOut = () => {
    setZoomLevel((z) => {
      const n = Math.max(0.5, Math.round((z - 0.1) * 10) / 10);
      if (n <= 1) {
        setPanX(0);
        setPanY(0);
      }
      return n;
    });
  };

  const zoomIn = () => setZoomLevel((z) => Math.min(5, Math.round((z + 0.1) * 10) / 10));

  const resetZoom = () => {
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  const handleDownload = async () => {
    const url = compareAfter;
    if (!url) return;
    try {
      const watermarkedImage = await addWatermarkToImage(url);
      const link = document.createElement('a');
      link.href = watermarkedImage;
      link.download = generatedImageDownloadFilename(Date.now());
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (err) {
      console.error('[ToneMapping] download failed:', err);
      alert(
        zh
          ? '下载失败，请登录账号后重试（云端图片需后端拉取才能加水印）。'
          : 'Download failed. Sign in and retry if the image is from cloud storage.',
      );
    }
  };

  useEffect(() => {
    zoomLevelPinchRef.current = zoomLevel;
  }, [zoomLevel]);

  useLayoutEffect(() => {
    if (!showCompare) return;
    const el = compareContainerRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoomLevel((prev) => {
        const newZoom = Math.max(0.5, Math.min(5, Math.round((prev + delta) * 10) / 10));
        if (newZoom <= 1) {
          setPanX(0);
          setPanY(0);
        }
        return newZoom;
      });
    };

    const opts: AddEventListenerOptions = { passive: false, capture: true };
    el.addEventListener('wheel', handleWheel, opts);
    return () => el.removeEventListener('wheel', handleWheel, opts);
  }, [showCompare]);

  useEffect(() => {
    if (!showCompare || !compareContainerRef.current) return;
    return attachComparePanelPinchZoom(compareContainerRef.current, {
      getZoom: () => zoomLevelPinchRef.current,
      setZoom: (z) => {
        zoomLevelPinchRef.current = z;
        setZoomLevel(z);
        if (z <= 1) {
          setPanX(0);
          setPanY(0);
        }
      },
      shouldIgnoreTouchTarget: (t) => {
        const el = t as HTMLElement | null;
        if (!el || typeof el.closest !== 'function') return false;
        return !!(el.closest('[data-tone-compare-slider]') || el.closest('input[type="range"]'));
      },
    });
  }, [showCompare]);

  panLatestRef.current = { panX, panY };

  useEffect(() => {
    if (!showCompare || !compareContainerRef.current || zoomLevel <= 1) return;

    const container = compareContainerRef.current;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const t = e.target as HTMLElement;
      if (t.closest('input[type="range"]')) return;
      if (t.closest('[data-tone-compare-slider]')) return;
      const { panX: px, panY: py } = panLatestRef.current;
      panDragRef.current = {
        active: true,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startPanX: px,
        startPanY: py,
      };
      setIsPanning(true);
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      const drag = panDragRef.current;
      if (!drag?.active) return;
      const dx = e.clientX - drag.startClientX;
      const dy = e.clientY - drag.startClientY;
      let newPanX = drag.startPanX + dx;
      let newPanY = drag.startPanY + dy;
      if (baseImgRef.current && compareContainerRef.current) {
        const imageRect = baseImgRef.current.getBoundingClientRect();
        const containerRect = compareContainerRef.current.getBoundingClientRect();
        const visW = imageRect.width;
        const visH = imageRect.height;
        const maxPanX = Math.max(0, (visW - containerRect.width) / 2);
        const maxPanY = Math.max(0, (visH - containerRect.height) / 2);
        newPanX = Math.max(-maxPanX, Math.min(maxPanX, newPanX));
        newPanY = Math.max(-maxPanY, Math.min(maxPanY, newPanY));
      } else {
        const maxPan = (zoomLevel - 1) * 200;
        newPanX = Math.max(-maxPan, Math.min(maxPan, newPanX));
        newPanY = Math.max(-maxPan, Math.min(maxPan, newPanY));
      }
      setPanX(newPanX);
      setPanY(newPanY);
    };

    const handleMouseUp = () => {
      if (panDragRef.current?.active) {
        panDragRef.current = null;
        setIsPanning(false);
      }
    };

    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      panDragRef.current = null;
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [showCompare, zoomLevel]);

  const dotBackgroundStyle = useMemo<React.CSSProperties>(() => ({ backgroundSize: '24px 24px' }), []);

  const updateImageBounds = useCallback(() => {
    if (!baseImgRef.current || !compareContainerRef.current) return;
    const imageRect = baseImgRef.current.getBoundingClientRect();
    const containerRect = compareContainerRef.current.getBoundingClientRect();
    const width = imageRect.width;
    if (!width) return;
    setImageBounds({ left: imageRect.left - containerRect.left, width });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => updateImageBounds();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, updateImageBounds]);

  useLayoutEffect(() => {
    if (!showCompare) return;
    updateImageBounds();
    const id = requestAnimationFrame(() => updateImageBounds());
    return () => cancelAnimationFrame(id);
  }, [showCompare, zoomLevel, panX, panY, fittedCompareFrame, compareDisplayPair, updateImageBounds]);

  const recalcFittedCompareFrame = useCallback(() => {
    const stage = compareStageRef.current;
    const nat = compareNaturalWH;
    if (!stage || !nat) {
      setFittedCompareFrame(null);
      return;
    }
    const fit = fitImageRectInBox(stage.clientWidth, stage.clientHeight, nat.w, nat.h);
    if (!fit) {
      setFittedCompareFrame(null);
      return;
    }
    setFittedCompareFrame((prev) => (prev && prev.w === fit.w && prev.h === fit.h ? prev : fit));
  }, [compareNaturalWH]);

  useLayoutEffect(() => {
    if (!showCompare) return;
    recalcFittedCompareFrame();
  }, [showCompare, recalcFittedCompareFrame]);

  useEffect(() => {
    if (!showCompare) return;
    const stage = compareStageRef.current;
    if (!stage) return;
    const ro = new ResizeObserver(() => recalcFittedCompareFrame());
    ro.observe(stage);
    return () => ro.disconnect();
  }, [showCompare, recalcFittedCompareFrame]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed inset-0 z-[2500] flex flex-col ${shellBg}`}
          style={{
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'San Francisco', 'Helvetica Neue', Helvetica, Arial, sans-serif",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className={`h-16 border-b flex items-center justify-between px-4 shrink-0 gap-4 backdrop-blur-md ${headerBg}`}>
            <div className="flex items-center gap-4 min-w-0">
              <div className="flex items-center gap-2 shrink-0">
                <h2 className="font-bold text-lg tracking-tight uppercase">
                  {zh ? '色调映射' : tK('tone_mapping')}
                </h2>
                {runningHubLinked ? (
                  <span
                    className="w-2 h-2 rounded-full bg-[#4ade80] shrink-0 shadow-[0_0_8px_rgba(74,222,128,0.65)]"
                    role="status"
                    title={
                      zh ? '后端已能连通 RunningHub（定期检测）' : 'Backend can reach RunningHub (periodic check)'
                    }
                  />
                ) : null}
              </div>
              {isGenerating ? (
                <div className="flex items-center gap-2 text-[#D7CDA1] text-sm min-w-0">
                  <div className="w-2 h-2 bg-[#D7CDA1] rounded-full animate-pulse" />
                  <span className="truncate">{progressDesc || (zh ? '生成中…' : 'Processing…')}</span>
                  <span className={`${textMuted} text-[11px] tabular-nums shrink-0`}>{Math.round(progressPercent)}%</span>
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleDownload}
                disabled={!compareAfter}
                className="px-4 py-2 bg-[#D7CDA1] text-[#101820] font-bold text-xs tracking-wider uppercase hover:bg-[#D7CDA1] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{ borderRadius: '5px' }}
              >
                {zh ? '下载' : 'Download'}
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label={tK('tone_mapping_close')}
                className={`p-2 transition-all ${btnGhost}`}
                style={{ borderRadius: '5px' }}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* 左侧：保持绑定逻辑不变，仅调整宽度/字体/文案位置 */}
            <aside
              className={`absolute left-0 top-16 bottom-0 w-80 p-6 overflow-y-auto no-scrollbar z-[2100] ${asideBg} ${
                isDarkMode ? 'backdrop-blur-md border-r border-white/10' : 'border-r border-zinc-200'
              }`}
            >
              <SidebarImageSlot
                title={tK('tone_mapping_load_shadow')}
                src={shadowImage}
                onAction={() => shadowInputRef.current?.click()}
                onDropFile={(f) => void applyShadowFile(f)}
                isDarkMode={isDarkMode}
              />
              <div className="mt-[10px] flex flex-col">
                <div className="mt-[5px]">
                  <SidebarImageSlot
                    title={tK('tone_mapping_load_main')}
                    src={mainImage}
                    onAction={() => mainInputRef.current?.click()}
                    onDropFile={(f) => void applyMainFile(f)}
                    isDarkMode={isDarkMode}
                  />
                </div>

                <button
                  type="button"
                  onClick={runGenerate}
                  disabled={!canGenerate}
                  className="w-full py-5 bg-[#D7CDA1] text-[#101820] font-bold text-sm tracking-wider uppercase hover:bg-[#D7CDA1] active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-6 leading-none"
                  style={{ borderRadius: '9px' }}
                >
                  {isGenerating ? (zh ? '生成中…' : '…') : zh ? '生成' : 'Generate'}
                </button>

                <div className={`mt-3 text-[10px] leading-relaxed font-normal ${textFaint}`}>
                  <p>{zh ? '注意：生成可能需要几分钟时间，请耐心等待。' : 'Note: Generation may take several minutes. Please wait patiently.'}</p>
                  <p className="mt-1">
                    {zh
                      ? 'AI生成因算法原因，并非一次达到满意效果，如不满意，点击按钮继续生成。'
                      : 'Due to the algorithm, AI generation may not be satisfying on the first try. If you are not satisfied, click the button to generate again.'}
                  </p>
                  <p
                    className={`mt-1 text-[10px] font-normal ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}
                  >
                    {zh
                      ? '图片加载异常时，请尝试切换其他图片后再返回查看。'
                      : 'If the preview loads incorrectly, try switching to another image, then return here to check.'}
                  </p>
                </div>

                <div className="flex-1 flex flex-col min-h-0">
                  <h2 className={`text-[10px] font-semibold uppercase tracking-wider mb-3 mt-6 ${textFaint}`}>
                    {zh ? '色调映射历史' : 'TONE MAPPING HISTORY'}
                  </h2>
                  <div className="flex-1 overflow-y-auto no-scrollbar flex flex-col gap-3 pb-6">
                    {toneHistory.length === 0 ? (
                      <p className={`text-[10px] text-center py-4 font-normal ${textFaint}`}>
                        {zh ? '生成后会出现在此，可多张留档' : 'Results appear here after generate.'}
                      </p>
                    ) : (
                      toneHistory.map((item) => {
                        const sel = currentToneMappingItem?.id === item.id;
                        return (
                          <div key={item.id} className="relative w-full">
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => void selectHistoryItem(item)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  void selectHistoryItem(item);
                                }
                              }}
                              className={`overflow-hidden border bg-black/20 hover:border-[#D7CDA1]/50 cursor-pointer transition-all group relative ${
                                sel ? 'border-[#D7CDA1] border-2' : 'border-white/10'
                              }`}
                              style={{ borderRadius: '9px' }}
                            >
                              <div className="aspect-video relative bg-black/40">
                                <RunningHubHistoryListThumb
                                  item={item}
                                  imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute top-2 left-2 z-[14] pointer-events-none">
                                  <RunningHubCosExpiryBadge timestamp={item.timestamp} lang={zh ? 'zh' : 'en'} />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center gap-2 p-2 z-[20]">
                                  <button
                                    type="button"
                                    onClick={(e) => void downloadToneHistoryItem(e, item)}
                                    className="px-3 py-1.5 bg-[#D7CDA1] text-[#101820] text-[8px] font-semibold uppercase hover:bg-[#D7CDA1] transition-all"
                                    style={{ borderRadius: '4px' }}
                                  >
                                    {zh ? '下载' : 'Download'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => deleteHistoryItem(e, item.id)}
                                    className="px-3 py-1.5 bg-red-600 text-white text-[8px] font-semibold uppercase hover:brightness-110 transition-all"
                                    style={{ borderRadius: '4px' }}
                                  >
                                    {zh ? '删除' : 'Delete'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </aside>

            <input ref={mainInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp" className="hidden" onChange={onPickMain} />
            <input ref={shadowInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp" className="hidden" onChange={onPickShadow} />

            {/* 右侧：大块滑杆对比 */}
            <main className={`flex-1 h-full relative ${isDarkMode ? 'bg-black' : 'bg-zinc-50'}`} style={dotBackgroundStyle}>
              {showCompare ? (
                <>
                  <div
                    ref={compareContainerRef}
                    className={`absolute top-0 right-0 bottom-0 left-80 overflow-hidden touch-none select-none ${isDarkMode ? 'bg-black' : 'bg-zinc-50'}`}
                    style={{
                      cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
                    }}
                    onWheelCapture={(e) => {
                      // 直接在 React 层兜底处理缩放，避免某些浏览器/触控板下监听不到 wheel
                      e.preventDefault();
                      e.stopPropagation();
                      const delta = (e as any).deltaY > 0 ? -0.1 : 0.1;
                      setZoomLevel((prev) => {
                        const newZoom = Math.max(0.5, Math.min(5, Math.round((prev + delta) * 10) / 10));
                        if (newZoom <= 1) {
                          setPanX(0);
                          setPanY(0);
                        }
                        return newZoom;
                      });
                    }}
                  >
                    <div
                      ref={compareStageRef}
                      className="absolute inset-0 flex items-center justify-center min-h-0 min-w-0 touch-none"
                      style={{
                        transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`,
                        transformOrigin: 'center center',
                        transition: isPanning ? 'none' : 'transform 0.1s ease-out',
                      }}
                    >
                      <div
                        key={compareSliderKey}
                        ref={compareInnerRef}
                        className="relative shrink-0 overflow-hidden bg-black"
                        style={
                          fittedCompareFrame
                            ? { width: fittedCompareFrame.w, height: fittedCompareFrame.h }
                            : { width: '100%', height: '100%', minHeight: 0 }
                        }
                      >
                        {/* 底层：生成结果；与上层主图同框同 object-fit，分割线由上层 clip-path 决定，避免条带 % 宽度与滑杆亚像素错位 */}
                        <img
                          ref={baseImgRef}
                          src={compareDisplayPair?.result ?? compareAfter!}
                          alt=""
                          className={`absolute inset-0 z-0 w-full h-full select-none pointer-events-none ${
                            compareDisplayPair ? 'object-fill' : 'object-contain object-center'
                          }`}
                          draggable={false}
                          onLoad={() => updateImageBounds()}
                        />
                        <img
                          src={compareDisplayPair?.base ?? compareBase!}
                          alt=""
                          className={`absolute inset-0 z-[2] w-full h-full select-none pointer-events-none ${
                            compareDisplayPair ? 'object-fill' : 'object-contain object-center'
                          }`}
                          style={{
                            clipPath: `inset(0 ${100 - comparePos}% 0 0)`,
                            transition: isDraggingSlider ? 'none' : 'clip-path 0.05s linear',
                          }}
                          draggable={false}
                          onLoad={() => updateImageBounds()}
                        />
                      </div>
                    </div>
                    {/* 滑杆：加宽透明命中区 + data 标记，避免缩放后错位点击到画布触发平移 */}
                    <div
                      data-tone-compare-slider
                      className="absolute top-0 bottom-0 z-20 flex justify-center cursor-ew-resize touch-none"
                      style={{
                        left: imageBounds ? `${imageBounds.left + (comparePos / 100) * imageBounds.width}px` : '50%',
                        width: 44,
                        marginLeft: -22,
                        transition: isDraggingSlider ? 'none' : 'left 0.05s linear',
                      }}
                      onPointerDown={(e) => {
                        e.nativeEvent.stopImmediatePropagation();
                        startCompareSliderPointerDrag(e, {
                          originalImageRef: baseImgRef,
                          imageContainerRef: compareContainerRef,
                          setSliderPercent: setComparePos,
                          setDragging: setIsDraggingSlider,
                          setImageBounds,
                        });
                      }}
                      onTouchStart={(e) => {
                        e.nativeEvent.stopImmediatePropagation();
                        startCompareSliderTouchDrag(e, {
                          originalImageRef: baseImgRef,
                          imageContainerRef: compareContainerRef,
                          setSliderPercent: setComparePos,
                          setDragging: setIsDraggingSlider,
                          setImageBounds,
                        });
                      }}
                    >
                      <div className="w-0.5 h-full shrink-0 bg-white/90 self-center" />
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-black pointer-events-none">
                        <svg
                          className="w-8 h-8 text-black rotate-90"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  {/* 去掉底部说明文字 */}
                </>
              ) : (
                <div className="flex-1" />
              )}
            </main>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ToneMappingModal;
